import {
  Content,
  TLink,
  TShopAdditionalContentMenu,
  TShopAuthStatus,
  TShopMenu,
} from "../../entities/content";
import { navContentStore } from "../../state/nav-content-store";
import { navStateStore } from "../../state/nav-state-store";
import {
  shopStateStore,
  ShopStateStoreTypes,
} from "../../state/shop-state-store";
import { ApplicationType, Application } from "../constants/app";
import { getUsername } from "../helper";

export function isShopAvailable(
  shop: TShopMenu | null,
  locale: string,
  app: ApplicationType,
): boolean {
  if (shop) {
    const { availableLocales } = shop;
    return (
      availableLocales !== undefined &&
      availableLocales.includes(locale) &&
      app === Application.shop
    );
  }
  return false;
}

function logoLinksToShop(shop: TShopMenu, locale: string): boolean {
  const { logoLinksToShopLocales } = shop;
  return logoLinksToShopLocales?.includes(locale) || false;
}

export function isShopAvailableAndLogoLinksToShop(
  locale: string,
  app: ApplicationType,
  shop: TShopMenu | null,
): boolean {
  if (shop) {
    return isShopAvailable(shop, locale, app) && logoLinksToShop(shop, locale);
  }
  return false;
}

export function modifyShopItemDependingOnMarket(
  locale: string,
  app: ApplicationType | undefined,
  content: Content,
): Content {
  const { shop, externalShop } = content;

  if (shop === null) return content;

  if (externalShop && app !== Application.shop) {
    return replaceShopWithExternalShop(content, shop, externalShop);
  }

  if (shopNotAvailableOnGlobalPages(shop, locale, app)) {
    return removeShopItemFromContent(content, shop);
  }

  return content;
}

function replaceShopWithExternalShop(
  content: Content,
  shop: TShopMenu,
  externalShop: TLink,
): Content {
  return {
    ...content,
    shop: {
      ...shop,
      ...externalShop,
    },
  };
}

function shopNotAvailableOnGlobalPages(
  shop: TShopMenu,
  locale: string,
  app: ApplicationType | undefined,
): boolean {
  return (
    !shop?.availableLocales.includes(locale) ||
    (shop?.availableLocalesOnlyForShop.includes(locale) &&
      app !== Application.shop)
  );
}

function removeShopItemFromContent(content: Content, shop: TShopMenu): Content {
  return {
    ...content,
    shop: {
      ...shop,
      text: "",
    },
  };
}

export function filterWishlistItemsByAuthStatus(
  menuItems: TShopAdditionalContentMenu[],
  authStatusList: TShopAuthStatus[],
): TShopAdditionalContentMenu[] {
  return menuItems?.filter((item) => {
    if (item.authStatus === null || item.authStatus === undefined) {
      return authStatusList.includes(null);
    }

    return authStatusList.includes(item.authStatus);
  });
}

export function getAvailableShopVariables(): {
  wishlistItemsNumber: number;
  username: string;
} {
  const { account } = navContentStore.state;
  const { loggedInState } = navStateStore.state;
  const { wishlistItemsNumber } = shopStateStore.state.shopInformation;

  const username = getUsername(loggedInState, account?.loggedInFallback ?? "");

  return { wishlistItemsNumber, username };
}

export function loadsShopPropsOnStore(props: ShopStateStoreTypes): void {
  shopStateStore.set("shopInformation", props.shopInformation);
}

function getWishlistIconName(): string {
  const { shopInformation } = shopStateStore.state;
  const wishlistNumber = shopInformation.wishlistItemsNumber;

  return wishlistNumber > 0 ? "heart-filled" : "heart";
}

export function handleFilledIcon(icon: string, numberOfItems: number): string {
  switch (true) {
    case numberOfItems === 0:
      return `${icon}`;
    case numberOfItems > 0:
      return `${icon}-filled`;
    default:
      return `${icon}`;
  }
}

export function getIconNameByMenuItemId(id: string): string | null {
  const iconsGetterFunctions: Record<string, () => string> = {
    wishlist: getWishlistIconName,
  };

  const getIconName = iconsGetterFunctions[id];

  if (typeof getIconName !== "function") return null;

  return getIconName();
}
